import { zodResolver } from '@hookform/resolvers/zod'
import { BadgeCheck, XCircle } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection, ReadonlyValue } from '@/components/common/FormBits'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { fmtMoney } from '../../../shared/format'
import { useApproveOrder, useRejectOrder } from '../../api'
import { LICENSE_TERMS, type Order } from '../../types'
import { toastError } from '../../ui'
import { Shell } from './RecordForms'

const today = () => new Date().toISOString().slice(0, 10)
const plusMonths = (n: number) => {
  const d = new Date()
  d.setMonth(d.getMonth() + n)
  return d.toISOString().slice(0, 10)
}

/** Sipariş onayı: lisans kapsamı ve bitiş tarihleri. Tarihlerden yenileme kayıtları otomatik açılır. */
export function ApproveOrderForm({ order, onDone }: { order: Order; onDone: () => void }) {
  const { t } = useTranslation()
  const approve = useApproveOrder()
  const future = (v: string) => !v || v >= today()
  const schema = z
    .object({
      branchLimit: z.coerce
        .number<string | number>()
        .int()
        .min(1, t('crm.order.branchInvalid'))
        .max(999, t('crm.order.branchInvalid')),
      term: z.enum(LICENSE_TERMS),
      validUntil: z.string().refine(future, t('crm.order.datePast')),
      maintenanceUntil: z.string().refine(future, t('crm.order.datePast')),
      supportUntil: z.string().refine(future, t('crm.order.datePast')),
    })
    .refine((v) => v.term === 'perpetual' || !!v.validUntil, {
      path: ['validUntil'],
      message: t('crm.order.validRequired'),
    })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      branchLimit: 1,
      term: 'yearly' as const,
      validUntil: plusMonths(12),
      maintenanceUntil: plusMonths(12),
      supportUntil: plusMonths(12),
    },
  })
  const term = useWatch({ control: form.control, name: 'term' })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await approve.mutateAsync({ id: order.id, license: v })
      toast.success(t('crm.order.approved', { number: order.number }))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  const date = (name: 'validUntil' | 'maintenanceUntil' | 'supportUntil', disabled = false) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <DateField id={`ap-${name}`} value={field.value} onChange={field.onChange} disabled={disabled} />
      )}
    />
  )

  return (
    <Shell
      icon={<BadgeCheck />}
      title={t('crm.order.approveTitle', { number: order.number })}
      hint={t('crm.order.approveHint')}
      pending={approve.isPending}
      onDone={onDone}
      onSubmit={(e) => void onSubmit(e)}
      submitLabel={t('crm.order.approve')}
    >
      <div className="space-y-6">
        <FormSection title={t('crm.order.sectionOrder')}>
          <Field id="ap-total" label={t('crm.order.total')}>
            <ReadonlyValue id="ap-total">{fmtMoney(order.total)}</ReadonlyValue>
          </Field>
          <Field id="ap-offer" label={t('crm.order.fromOffer')}>
            <ReadonlyValue id="ap-offer">{order.offerNumber}</ReadonlyValue>
          </Field>
        </FormSection>
        <FormSection title={t('crm.order.sectionLicense')}>
          <Field id="ap-branch" label={t('crm.order.branchLimit')} error={errors.branchLimit?.message}>
            <Input
              id="ap-branch"
              type="number"
              min={1}
              max={999}
              inputMode="numeric"
              {...form.register('branchLimit')}
            />
          </Field>
          <Field id="ap-term" label={t('crm.order.term')}>
            <FormSelect
              id="ap-term"
              control={form.control}
              name="term"
              options={LICENSE_TERMS.map((k) => ({ value: k, label: t(`crm.licenseTerm.${k}`) }))}
            />
          </Field>
          <Field
            id="ap-validUntil"
            label={t('crm.order.validUntil')}
            error={errors.validUntil?.message}
            hint={term === 'perpetual' ? t('crm.order.perpetualHint') : undefined}
          >
            {date('validUntil', term === 'perpetual')}
          </Field>
          <Field
            id="ap-maintenanceUntil"
            label={t('crm.order.maintenanceUntil')}
            error={errors.maintenanceUntil?.message}
            hint={t('crm.order.optional')}
          >
            {date('maintenanceUntil')}
          </Field>
          <Field
            id="ap-supportUntil"
            label={t('crm.order.supportUntil')}
            error={errors.supportUntil?.message}
            hint={t('crm.order.optional')}
            full
          >
            {date('supportUntil')}
          </Field>
        </FormSection>
      </div>
    </Shell>
  )
}

export function RejectOrderForm({ order, onDone }: { order: Order; onDone: () => void }) {
  const { t } = useTranslation()
  const reject = useRejectOrder()
  const schema = z.object({ note: z.string().trim().min(3, t('crm.order.rejectNoteRequired')).max(500) })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { note: '' } })
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await reject.mutateAsync({ id: order.id, note: v.note })
      toast.success(t('crm.order.rejected', { number: order.number }))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Shell
      icon={<XCircle />}
      title={t('crm.order.rejectTitle', { number: order.number })}
      hint={t('crm.order.rejectHint')}
      pending={reject.isPending}
      onDone={onDone}
      onSubmit={(e) => void onSubmit(e)}
      submitLabel={t('crm.order.reject')}
    >
      <FormSection>
        <Field id="rj-note" label={t('crm.order.rejectNote')} error={form.formState.errors.note?.message} full>
          <Textarea id="rj-note" rows={3} maxLength={500} {...form.register('note')} />
        </Field>
      </FormSection>
    </Shell>
  )
}
